import Box from '@mui/material/Box';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { Page } from '@/components/page';
import { FeatureGuard } from '@/components/feature-guard';
import { QuickEventDialog } from '@/components/timeline/quick-event-dialog';
import { TimelineFilters } from '@/components/timeline/timeline-filters';
import { TimelineHeader } from '@/components/timeline/timeline-header';
import { TimelineListView } from '@/components/timeline/timeline-list-view';
import { TimelineMapView } from '@/components/timeline/timeline-map-view';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { filterEventsByFilters, type TimelineFilters as ITimelineFilters } from '@/lib/timeline-utils';
import type { EntitySummary, EventSummary, NarrativeSummary } from '@/lib/types';

type ViewMode = 'list' | 'map';

function TimelineContent() {
  const { novel: currentNovel, getSummaries } = useNovelRepository();
  const selectedNovelId = currentNovel?.id;

  // State
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [filters, setFilters] = useState<ITimelineFilters>({
    locationIds: [],
    includeSublocations: true,
    characterIds: [],
    organizationIds: [],
    narrativeStatus: [],
  });
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);

  // Data deriving
  const events = getSummaries<EventSummary>('events');
  const locations = getSummaries<EntitySummary>('locations');
  const characters = getSummaries<EntitySummary>('characters');
  const organizations = getSummaries<EntitySummary>('organizations');
  const scenes = getSummaries<NarrativeSummary>('scenes');

  // Filter events by filters
  const filteredEvents = useMemo(() => {
    if (!events) return [];
    return filterEventsByFilters(events, filters, locations || []);
  }, [events, filters, locations]);

  // Sort filtered events by date
  const sortedEvents = useMemo(() => {
    return [...filteredEvents].sort((a, b) => {
      const dateA = a.date || '';
      const dateB = b.date || '';
      return dateA.localeCompare(dateB);
    });
  }, [filteredEvents]);

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: ViewMode | null
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      locationIds: [],
      includeSublocations: true,
      characterIds: [],
      organizationIds: [],
      narrativeStatus: [],
    });
  };

  const { t } = useTranslation();

  return (
    <AuthGuard>
      <Page 
        title={t('timeline.title')}
        description={t('timeline.description')}
        layoutMode="workspace"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', bgcolor: 'background.default' }}>
          {/* Header */}
        <TimelineHeader
          filters={filters}
          onClearFilters={handleClearFilters}
          onQuickCreate={() => setIsQuickCreateOpen(true)}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
        />

        {/* Main Content Area */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left Panel: Filters */}
          <TimelineFilters
            filters={filters}
            onFiltersChange={setFilters}
            locations={locations || []}
            characters={characters || []}
            organizations={organizations || []}
          />

          {/* Main View Area */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {viewMode === 'list' ? (
              <TimelineListView
                events={sortedEvents}
                scenes={scenes || []}
                locations={locations || []}
                characters={characters || []}
                organizations={organizations || []}
              />
            ) : (
              <TimelineMapView
                events={sortedEvents}
                locations={locations || []}
                scenes={scenes || []}
              />
            )}
          </Box>
        </Box>

        {/* Quick Create Dialog */}
        <QuickEventDialog
          open={isQuickCreateOpen}
          onClose={() => setIsQuickCreateOpen(false)}
        />
        </Box>
      </Page>
    </AuthGuard>
  );
}

export const Route = createFileRoute('/timeline')({
  component: TimelinePage,
});

export default function TimelinePage() {
  return (
    <FeatureGuard featureKey="should_display_timeline_page">
      <TimelineContent />
    </FeatureGuard>
  );
}
